import { Alert, Button, Stack } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { ConfirmationCode } from '@vy/types';
import { ConfirmationCodeSchema } from '@vy/types';
import { extract, isNotNullOrUndefined, purge } from '@vy/utils';
import React, { useState } from 'react';
import { useInitializeInputs } from '../../../RegisterConfirmationPage/register-confirmation-page-components/RegisterConfirmationPageForm';
import { InfoBox } from '../../../users-components/InfoBox';
import { VerificationCodeInputFields } from '../../../users-components/VerificationCodeInputFields';
import { BackToLogin } from '../BackToLogin';

type VerifyCodeFormProps = {
  onNext: (code: ConfirmationCode) => void;
};

export const VerifyCodeForm: React.FC<VerifyCodeFormProps> = ({ onNext }) => {
  const [error, setError] = useState<string | null>(null);
  const refs = useInitializeInputs();

  const handleContinueClick = () => {
    const code = extractCodeFromInputs();

    if (isCodeValid(code)) {
      onNext(code);
    }

    setError('The verification code you entered is incorrect. Please try again.');
  };

  const extractCodeFromInputs = () => {
    const inputsElements = purge(extract(refs, 'current'));

    return inputsElements.map(({ value }) => value).join('');
  };

  const isCodeValid = (code: string) => {
    return ConfirmationCodeSchema.safeParse(code).success;
  };

  return (
    <Stack spacing={4} sx={{ width: '456px', padding: 2 }}>
      <InfoBox
        title="Enter Verification Code"
        description="We've sent a verification code to your email. Enter it below along with your new password."
      />

      <VerificationCodeInputFields refs={refs} />

      <Button
        variant="contained"
        fullWidth
        onClick={handleContinueClick}
        size="large"
        endIcon={<VyIcon icon="ArrowSmallRight" />}
      >
        Continue
      </Button>

      {isNotNullOrUndefined(error) && <Alert severity="error">{error}</Alert>}
      <BackToLogin />
    </Stack>
  );
};
